import { showErrorNotification } from "@/utils/form-submission-notifications"
import { useFileDialog } from "@mantine/hooks"
import { useTranslation } from "react-i18next"

export const ImageMIMETypes = [
  "image/jpg",
  "image/png",
  "image/jpeg",
  "image/bmp",
  "image/gif",
]

const defaultAcceptedMIMETypes = [
  "image/jpg",
  "image/png",
  "image/jpeg",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "audio/mpeg",
  "audio/wav",
  "video/mp4",
  "video/webm",
  "text/plain",
  "application/rtf",
  "image/bmp",
  "image/gif",
]

const defaultMaxSize = 200

interface Props {
  acceptedMIMETypes?: string[]
  multiple?: boolean
  maxSize?: number
  initialValues?: File[]
}

export function useDefaultFileDialog(props?: Props) {
  const { t } = useTranslation("app_server")
  const acceptedMIMETypes = props?.acceptedMIMETypes ?? defaultAcceptedMIMETypes
  const multiple = props?.multiple
  const maxSize = props?.maxSize ?? defaultMaxSize
  const [files, setFiles] = React.useState<File[] | null>(
    props?.initialValues ?? null
  )

  const handleChangeFiles = (fileList: FileList | null) => {
    if (fileList === null) {
      setFiles(null)
      return
    }
    let files: File[] = []
    for (let i = 0; i < fileList.length; i++) {
      const file = fileList.item(i)
      if (file) {
        let rejections = []

        if (!file.type || !acceptedMIMETypes.includes(file.type)) {
          rejections.push(
            t("FileDialog_RejectionMessage_Type", {
              fileType: file.type.includes("/")
                ? file.type.split("/").at(1)
                : file.type,
            })
          )
        }
        if (file.size > maxSize * 1024 * 1024) {
          rejections.push(
            t("FileDialog_RejectionMessage_Size", {
              maxSize: maxSize,
            })
          )
        }
        if (rejections.length > 0) {
          showErrorNotification(
            t("FileDialog_RejectionMessage", {
              fileName: file.name,
              rejections: rejections.join(", "),
            })
          )
        } else {
          files.push(file)
        }
      }
    }
    setFiles(files)
  }

  const fileDialog = useFileDialog({
    onChange: handleChangeFiles,
    accept: acceptedMIMETypes.join(","),
    multiple,
  })

  return { files, setFiles, fileDialog }
}
